import { getRoomsSetByCount } from "../../../utils/LDSHelpers";
import { preparePassengersInfoForLdsSave } from "../../../utils/passengers/ldsPassengerMapper";

/** Keep in sync with PassengersData.ROOM_KEYS / buildInitialRoomsAndPaxInfo.RK */
const ROOM_KEYS = {
  ROOM_SET: "roomSet",
  ROOM_TYPE: "roomType",
  SEQ_NUM: "@seqNum",
  LDS_DEAL_ID: "ldsDealId",
};

/**
 * Mirrors OrganizedTour extended-deal row matching (SelectedResultContext vacation-packages matrix)
 * without mutating pricing — only room identifiers required for LDS <room>/<roomSet>.
 */
function collectExtendedCandidates(roomPax, extendedDealApiData) {
  const extended = Array.isArray(extendedDealApiData) ? extendedDealApiData : [];
  const adults = Array.isArray(roomPax?.adults) ? roomPax.adults.length : 0;
  const kids = Array.isArray(roomPax?.kids) ? roomPax.kids.length : 0;
  const infants = Array.isArray(roomPax?.infants) ? roomPax.infants.length : 0;
  const neededRoomSets = getRoomsSetByCount(adults, kids);
  const out = [];
  for (const row of extended) {
    if (!row || typeof row !== "object") continue;
    const roomSet = row.roomSet;
    if (!neededRoomSets.includes(roomSet)) continue;
    const infantFare = row.infantFare;
    if (infants > 0 && !infantFare) continue;
    out.push(row);
  }
  return out;
}

/**
 * @param {unknown} roomsAndPaxInfo
 * @param {unknown} extendedDealApiData
 * @param {unknown} packageLdsDealId — linkedFlights / result fallback for per-room ldsDealId
 */
export function buildOrganizedTourPassengersInfoForLdsSave(
  roomsAndPaxInfo,
  extendedDealApiData,
  packageLdsDealId
) {
  const clone = JSON.parse(
    JSON.stringify(Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [])
  );
  const pkgId =
    packageLdsDealId != null && String(packageLdsDealId).trim() !== ""
      ? String(packageLdsDealId).trim()
      : null;
  const roomSetMeta = [];

  for (let i = 0; i < clone.length; i++) {
    const rp = clone[i];
    const existing = rp.room && typeof rp.room === "object" ? { ...rp.room } : {};
    const existingRs =
      existing[ROOM_KEYS.ROOM_SET] ?? existing.roomSet ?? existing.RoomSet;
    const hadRs = existingRs != null && String(existingRs).trim() !== "";

    if (hadRs) {
      roomSetMeta.push({
        idx: i,
        source: "existing",
        value: String(existingRs).trim(),
      });
      continue;
    }

    const candidates = collectExtendedCandidates(rp, extendedDealApiData);
    if (candidates.length) {
      candidates.sort(
        (a, b) => Number(a.totalFare || 0) - Number(b.totalFare || 0)
      );
      const pick = candidates[0];
      const pickedLdsRaw = pick.ldsDealId ?? pick.lds_deal_id;
      const pickedLds =
        pickedLdsRaw != null && String(pickedLdsRaw).trim() !== ""
          ? String(pickedLdsRaw).trim()
          : pkgId;
      rp.room = {
        ...existing,
        [ROOM_KEYS.ROOM_SET]: pick.roomSet,
        [ROOM_KEYS.ROOM_TYPE]:
          pick.roomType ?? existing[ROOM_KEYS.ROOM_TYPE] ?? pick.roomTypeName,
        [ROOM_KEYS.SEQ_NUM]: pick["@seqNum"] ?? existing[ROOM_KEYS.SEQ_NUM],
        [ROOM_KEYS.LDS_DEAL_ID]:
          pickedLds ?? existing[ROOM_KEYS.LDS_DEAL_ID] ?? existing.ldsDealId,
      };
      roomSetMeta.push({
        idx: i,
        source: "extended_match",
        value: pick.roomSet != null ? String(pick.roomSet) : null,
      });
    } else {
      const adults = Array.isArray(rp?.adults) ? rp.adults.length : 0;
      const kids = Array.isArray(rp?.kids) ? rp.kids.length : 0;
      const occupancyCandidates = getRoomsSetByCount(adults, kids);
      let derivedSet = null;
      let derivedRow = null;

      for (const rs of occupancyCandidates) {
        const extMatch = (Array.isArray(extendedDealApiData) ? extendedDealApiData : []).find(
          (row) => row && String(row.roomSet ?? row.RoomSet ?? "").trim() === rs
        );
        if (extMatch) {
          derivedSet = rs;
          derivedRow = extMatch;
          break;
        }
      }
      if (!derivedSet && occupancyCandidates.length) {
        derivedSet = occupancyCandidates[0];
      }

      if (derivedSet) {
        const pickedLdsRaw = derivedRow?.ldsDealId ?? derivedRow?.lds_deal_id;
        const pickedLds =
          pickedLdsRaw != null && String(pickedLdsRaw).trim() !== ""
            ? String(pickedLdsRaw).trim()
            : pkgId;
        rp.room = {
          ...existing,
          [ROOM_KEYS.ROOM_SET]: derivedSet,
          ...(derivedRow
            ? {
                [ROOM_KEYS.ROOM_TYPE]:
                  derivedRow.roomType ??
                  existing[ROOM_KEYS.ROOM_TYPE] ??
                  derivedRow.roomTypeName,
                [ROOM_KEYS.SEQ_NUM]:
                  derivedRow["@seqNum"] ?? existing[ROOM_KEYS.SEQ_NUM],
              }
            : {}),
          [ROOM_KEYS.LDS_DEAL_ID]:
            pickedLds ?? existing[ROOM_KEYS.LDS_DEAL_ID] ?? existing.ldsDealId,
        };
        roomSetMeta.push({
          idx: i,
          source: derivedRow ? "occupancy_extended_match" : "occupancy_inferred",
          value: derivedSet,
        });
      } else {
        roomSetMeta.push({ idx: i, source: "missing", value: null });
      }
    }
  }

  const allRoomsHaveRoomSet = roomSetMeta.every(
    (m) => m.value != null && String(m.value).trim() !== ""
  );

  return {
    passengersInfo: preparePassengersInfoForLdsSave(clone),
    roomSetMeta,
    allRoomsHaveRoomSet,
    roomSet_source: roomSetMeta.map((m) => m.source).join("|"),
    roomSet_value: roomSetMeta.map((m) => (m.value == null ? "" : String(m.value))).join("|"),
  };
}
